import { message } from 'antd';
import { getErrorMessage } from './getErrorMessage';

/**
 * TCX-900 — telling a paying customer to "activate your subscription" because
 * WE could not reach Stripe is the bug this ticket exists to kill. The backend
 * now answers a retryable 503 for that case, tagged with a machine-readable
 * code; this module is the frontend half of that contract.
 *
 * We branch on the CODE, never on the server's prose: the wording below is the
 * frontend's own, so backend copy can change without silently degrading the UI
 * into "Request failed with status code 503".
 * Mirrors `SUBSCRIPTION_UNVERIFIABLE_CODE` in packages/nestJs/src/common/types.
 * There is no shared package to declare it once, so the copies are held
 * together mechanically instead: `subscriptionError.spec.ts` reads the backend
 * declaration and fails when the literals drift. Without that, a rename on
 * either side leaves both suites green and the user back on the generic
 * "Request failed with status code 503".
 */
export const SUBSCRIPTION_UNVERIFIABLE_CODE = 'SUBSCRIPTION_UNVERIFIABLE';

/**
 * What the user reads. Deliberately about OUR provider, not their account: this
 * state means we could not verify a subscription, which is never evidence that
 * the customer failed to pay. It is also explicitly retryable — the same click
 * usually works moments later.
 */
export const SUBSCRIPTION_UNVERIFIABLE_MESSAGE =
  "We're having trouble reaching our payment provider. Please try again in a moment.";

/**
 * An `Error` that survived an api-layer rethrow with the server's error code
 * attached. The api wrappers flatten AxiosErrors into plain `Error`s (callers
 * compare `error.message`), which used to discard the status and the code —
 * so the code rides along on this field.
 */
export interface CodedError extends Error {
  code?: string;
}

/** Attach a server error code to a flattened api error, if there was one. */
export const withErrorCode = (error: Error, code?: unknown): CodedError => {
  if (typeof code === 'string' && code) {
    (error as CodedError).code = code;
  }
  return error;
};

/**
 * Whether this failure means "we could not verify the subscription", in either
 * shape the app sees it: a raw AxiosError, or an api-layer `Error` carrying the
 * code. A bare 503 is NOT enough — an unrelated outage must not be reported as
 * a payment-provider problem.
 */
export const isSubscriptionUnverifiableError = (error: unknown): boolean => {
  const err = error as {
    code?: unknown;
    response?: { data?: { error?: unknown } };
  } | null;

  return (
    err?.code === SUBSCRIPTION_UNVERIFIABLE_CODE ||
    err?.response?.data?.error === SUBSCRIPTION_UNVERIFIABLE_CODE
  );
};

/**
 * Report a failed write. One place decides how this class of failure looks, so
 * every gated action (book a meeting, start a chat, approve a reschedule)
 * reacts to a Stripe outage the same way: a WARNING that invites a retry, never
 * an error that reads like the action was rejected.
 */
export const notifyActionError = (error: unknown, fallback?: string): void => {
  if (isSubscriptionUnverifiableError(error)) {
    message.warning(SUBSCRIPTION_UNVERIFIABLE_MESSAGE);
    return;
  }
  message.error(getErrorMessage(error, fallback));
};
